import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Heart, CalendarDays, Compass, Sparkles, Crown, Lock,
  Ticket, Building2, Globe, Mail, Phone, ShieldCheck, Clock, Gift, Check, Minus,
  HelpCircle, Infinity as InfinityIcon, CircleDot, CircleSlash, Loader2, Info,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeSwitcher from "@/components/ThemeSwitcher";
import { useTranslation } from "@/i18n";
import { useAuth } from "@/auth/AuthContext";
import {
  planOf, roleOf, subActive, isUnlimited, standardCredits, advancedCredits, POOL_MAX,
  aiAccess, MODE_LEVEL, strategistPlanCredits, STRATEGIST_PLAN_POOL,
} from "@/lib/access";
import { fetchPublicCompany } from "@/lib/company";
import { fetchPublicContent } from "@/lib/siteContent";
import { ignore } from "@/lib/log";
import { fetchPaymentConfig, createCheckout, fetchCheckoutStatus } from "@/lib/payments";
import { fetchPublicPackages } from "@/lib/packages";
import { toast } from "sonner";

const AI_TEAM = [
  { key: "assistant", icon: CalendarDays },
  { key: "strategy", icon: Compass },
  { key: "support", icon: Heart },
];

// Fallback pricing (used until the editable package config loads). Currency strings not translated.
const PRICING_DEF = {
  service: { icon: Lock },
  assistant: { icon: Sparkles, month: "4.99 €", year: "41.95 €" },
  strategist: { icon: Crown, month: "12.99 €", year: "124.95 €", trial: "4.99 €" },
};

// Feature matrix fallback: [service, assistant, strategist].
const COMPARE_DEF = [
  { k: "calendar", v: [true, true, true] },
  { k: "chronicles", v: [true, true, true] },
  { k: "tasks", v: [true, true, true] },
  { k: "month", v: [true, true, true] },
  { k: "standardPool", v: [false, "50 / mo", "1000 / mo"] },
  { k: "advancedPool", v: [false, "10 / mo", "shared"] },
  { k: "trial", v: [false, false, "trialValue"] },
  { k: "renewal", v: [false, "renewalValue", "renewalValue"] },
];

// Per-plan checkout buttons. Package ids match backend derived SKUs.
const PLAN_BUTTONS_DEF = {
  assistant: [
    { id: "assistant_monthly", labelKey: "monthlyBtn", price: "4.99 €" },
    { id: "assistant_annual", labelKey: "annualBtn", price: "41.95 €" },
  ],
  strategist: [
    { id: "strategist_trial", labelKey: "trialBtn", price: "4.99 €" },
    { id: "strategist_monthly", labelKey: "monthlyBtn", price: "12.99 €" },
    { id: "strategist_annual", labelKey: "annualBtn", price: "124.95 €" },
  ],
};

// Format an EUR amount like the existing strings ("35 €", "3.99 €").
function euro(n) {
  if (n == null || isNaN(n)) return null;
  const v = Number(n);
  return `${Number.isInteger(v) ? v : v.toFixed(2)} €`;
}

export default function Palvelumme() {
  const navigate = useNavigate();
  const { t, locale, lang } = useTranslation();
  const { user, refreshUser } = useAuth();
  const [company, setCompany] = useState(null);
  const [content, setContent] = useState({ additionalInfo: [], faq: [] });
  const [openFaq, setOpenFaq] = useState(0);
  const [stripeReady, setStripeReady] = useState(false);
  const [busyPkg, setBusyPkg] = useState(null);
  const [discountCode, setDiscountCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [pkgCfg, setPkgCfg] = useState(null);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    fetchPublicCompany().then(setCompany).catch(() => setCompany(null));
    fetchPublicContent().then((c) => setContent(c || { additionalInfo: [], faq: [] })).catch(() => setContent({ additionalInfo: [], faq: [] }));
    fetchPaymentConfig().then((c) => setStripeReady(!!c.configured)).catch(() => setStripeReady(false));
    fetchPublicPackages()
      .then((list) => setPkgCfg(Object.fromEntries(list.map((p) => [p.planId, p]))))
      .catch(() => setPkgCfg(null));
  }, []);

  // Live pricing/comparison derived from the editable package config (single
  // source of truth), falling back to the static defaults until it loads.
  const PRICING = useMemo(() => {
    const c = pkgCfg;
    if (!c) return PRICING_DEF;
    return {
      service: { icon: Lock },
      assistant: { icon: Sparkles, month: euro(c.assistant?.pricing?.monthly) || PRICING_DEF.assistant.month, year: euro(c.assistant?.pricing?.yearly) || PRICING_DEF.assistant.year },
      strategist: { icon: Crown, month: euro(c.strategist?.pricing?.monthly) || PRICING_DEF.strategist.month, year: euro(c.strategist?.pricing?.yearly) || PRICING_DEF.strategist.year, trial: euro(c.strategist?.trial?.price) || PRICING_DEF.strategist.trial },
    };
  }, [pkgCfg]);

  const PLAN_BUTTONS = useMemo(() => {
    const c = pkgCfg;
    if (!c) return PLAN_BUTTONS_DEF;
    return {
      assistant: [
        { id: "assistant_monthly", labelKey: "monthlyBtn", price: euro(c.assistant?.pricing?.monthly) || PLAN_BUTTONS_DEF.assistant[0].price },
        { id: "assistant_annual", labelKey: "annualBtn", price: euro(c.assistant?.pricing?.yearly) || PLAN_BUTTONS_DEF.assistant[1].price },
      ],
      strategist: [
        ...(c.strategist?.trial?.available ? [{ id: "strategist_trial", labelKey: "trialBtn", price: euro(c.strategist?.trial?.price) || PLAN_BUTTONS_DEF.strategist[0].price }] : []),
        { id: "strategist_monthly", labelKey: "monthlyBtn", price: euro(c.strategist?.pricing?.monthly) || PLAN_BUTTONS_DEF.strategist[1].price },
        { id: "strategist_annual", labelKey: "annualBtn", price: euro(c.strategist?.pricing?.yearly) || PLAN_BUTTONS_DEF.strategist[2].price },
      ],
    };
  }, [pkgCfg]);

  const COMPARE = useMemo(() => {
    const c = pkgCfg;
    if (!c) return COMPARE_DEF;
    const a = c.assistant?.credits || {};
    const s = c.strategist?.credits || {};
    return COMPARE_DEF.map((row) => {
      if (row.k === "standardPool") return { ...row, v: [false, `${a.standard ?? 50} / mo`, `${s.total ?? 1000} / mo`] };
      if (row.k === "advancedPool") return { ...row, v: [false, `${a.advanced ?? 10} / mo`, s.shared ? "shared" : `${s.advanced ?? 1000} / mo`] };
      return row;
    });
  }, [pkgCfg]);

  // On return from Stripe: poll the payment status, then activate + refresh.
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let cancelled = false;
    setVerifying(true);
    const poll = async (attempt = 0) => {
      try {
        const res = await fetchCheckoutStatus(sid);
        if (cancelled) return;
        if (res.payment_status === "paid") {
          await refreshUser();
          toast.success(t("palvelumme.pricing.success"));
          setVerifying(false);
          setParams({}, { replace: true });
          return;
        }
        if (res.status === "expired") {
          toast.error(t("palvelumme.pricing.failed"));
          setVerifying(false);
          setParams({}, { replace: true });
          return;
        }
      } catch {
        ignore(); // transient status error — the retry below keeps polling
      }
      if (attempt >= 6) {
        setVerifying(false);
        setParams({}, { replace: true });
        return;
      }
      setTimeout(() => poll(attempt + 1), 2000);
    };
    poll();
    return () => { cancelled = true; };
    // One-time mount effect: reads the initial ?session_id once and polls to
    // completion. Adding params/refreshUser/setParams/t would restart polling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckout = async (packageId) => {
    if (!stripeReady) {
      toast.error(t("palvelumme.pricing.stripeMissing"));
      return;
    }
    setBusyPkg(packageId);
    try {
      const { url } = await createCheckout(packageId, discountCode);
      window.location.href = url;
    } catch (e) {
      toast.error(e.response?.data?.detail || t("palvelumme.pricing.failed"));
      setBusyPkg(null);
    }
  };

  const plan = planOf(user);
  const role = roleOf(user);
  const unlimited = isUnlimited(user);
  const sub = user?.subscription || {};
  const status = sub.status || "active";
  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(locale) : "—");

  const roleAvailability = (key) => {
    const acc = aiAccess(user, MODE_LEVEL[key] || "assistant");
    if (acc.unlimited || acc.allowed) return { label: t("palvelumme.avail.available"), tone: "ok" };
    if (acc.reason === "no_credits") return { label: t("palvelumme.avail.outOfCredits"), tone: "warn" };
    return { label: t("palvelumme.avail.upgrade"), tone: "off" };
  };

  const cell = (val) => {
    if (val === true) return <Check className="h-4 w-4 text-emerald-600 mx-auto" />;
    if (val === false) return <Minus className="h-4 w-4 text-slate-300 mx-auto" />;
    if (val === "∞") return <span className="inline-flex items-center gap-1 justify-center text-slate-900 font-medium"><InfinityIcon className="h-4 w-4" /></span>;
    if (val === "shared") return <span className="text-slate-600 text-[13px]">{t("palvelumme.compare.shared")}</span>;
    if (val === "trialValue") return <span className="text-slate-700">{t("palvelumme.compare.trialValue")}</span>;
    if (val === "renewalValue") return <span className="text-slate-700">{t("palvelumme.compare.renewalValue")}</span>;
    return <span className="text-slate-700">{val}</span>;
  };

  const creditRow = (label, value, max, testid) => (
    <div className="flex items-center justify-between" data-testid={testid}>
      <span className="text-sm text-slate-600">{label}</span>
      <span className="font-mono-tab text-sm font-semibold text-slate-900">
        {unlimited || value === "unlimited" ? t("palvelumme.unlimited") : `${value} / ${max}`}
      </span>
    </div>
  );

  const staticFaqItems = t("palvelumme.faq.items") || [];
  const pick = (obj) => (obj && (obj[lang] || obj.en || obj.fi)) || "";
  // Founder-managed FAQ takes precedence; fall back to the static i18n FAQ.
  const customFaq = (content.faq || [])
    .map((it) => ({ q: pick(it.question), a: pick(it.answer) }))
    .filter((it) => it.q || it.a);
  const faqItems = customFaq.length ? customFaq : staticFaqItems;
  const infoItems = (content.additionalInfo || [])
    .map((it) => ({ title: pick(it.title), body: pick(it.body) }))
    .filter((it) => it.title || it.body);

  return (
    <div className="min-h-screen bg-background transition-colors duration-300 flex flex-col" data-testid="palvelumme-page">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-card/80 border-b border-border">
        <div className="mx-auto max-w-5xl px-4 sm:px-8 py-3 flex items-center gap-2">
          <button onClick={() => navigate("/")} className="h-9 w-9 rounded-full hover:bg-accent flex items-center justify-center" data-testid="palvelumme-back" aria-label={t("palvelumme.back")}>
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex-1 flex items-center gap-2">
            <span className="font-display text-base font-semibold tracking-tight text-foreground">Chronicles</span>
          </div>
          <LanguageSwitcher />
          <ThemeSwitcher />
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-5xl px-5 sm:px-8 py-8 sm:py-12 space-y-12">
        {verifying ? (
          <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4 flex items-center gap-3" data-testid="palvelumme-verifying">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <span className="text-sm text-slate-700">{t("palvelumme.pricing.verifying")}</span>
          </div>
        ) : null}
        {/* 1. Hero */}
        <section className="surface-card p-8 sm:p-12" data-testid="palvelumme-hero">
          <p className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground mb-3">{t("palvelumme.overline")}</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.05]" data-testid="palvelumme-title">
            {t("palvelumme.heroHeadline")}
          </h1>
          <p className="mt-5 text-base sm:text-lg font-light text-slate-600 max-w-2xl leading-relaxed">{t("palvelumme.heroBody")}</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 max-w-2xl">
            <div className="rounded-2xl border border-border bg-accent/50 p-4">
              <p className="text-[11px] uppercase tracking-[0.18em] text-emerald-700 font-medium mb-1">{t("settings.plans.service")}</p>
              <p className="text-sm text-slate-700 leading-relaxed">{t("palvelumme.heroFree")}</p>
            </div>
            <div className="rounded-2xl border border-border bg-accent/50 p-4">
              <p className="text-[11px] uppercase tracking-[0.18em] text-slate-700 font-medium mb-1">Premium</p>
              <p className="text-sm text-slate-700 leading-relaxed">{t("palvelumme.heroPremium")}</p>
            </div>
          </div>
        </section>

        {/* 2. Current subscription */}
        <section className="surface-card p-6" data-testid="palvelumme-subscription">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="h-4 w-4 text-slate-700" />
            <h2 className="font-display text-lg font-semibold text-foreground">{t("palvelumme.currentTitle")}</h2>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">{t("palvelumme.plan")}</span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white text-xs font-medium px-3 py-1" data-testid="palvelumme-plan-badge">
                  {plan === "strategist" ? <Crown className="h-3 w-3" /> : plan === "assistant" ? <Sparkles className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                  {t(`settings.plans.${plan}`)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">{t("palvelumme.role")}</span>
                <span className="text-sm font-medium text-slate-900">{t(`settings.roles.${role}`)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">{t("palvelumme.status")}</span>
                <span className={["inline-flex rounded-full text-[11px] font-medium px-2 py-0.5", subActive(user) ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"].join(" ")} data-testid="palvelumme-status">
                  {t(`palvelumme.statuses.${status}`) || status}
                </span>
              </div>
            </div>
            <div className="space-y-2.5 sm:border-l sm:border-border sm:pl-5">
              {unlimited ? (
                <>
                  {creditRow(t("palvelumme.standardPool"), "unlimited", null, "palvelumme-standard-credits")}
                  {creditRow(t("palvelumme.advancedPool"), "unlimited", null, "palvelumme-advanced-credits")}
                </>
              ) : plan === "strategist" ? (
                creditRow(t("palvelumme.aiCredits"), strategistPlanCredits(user), STRATEGIST_PLAN_POOL, "palvelumme-strategist-credits")
              ) : (
                <>
                  {creditRow(t("palvelumme.standardPool"), standardCredits(user), POOL_MAX.assistant, "palvelumme-standard-credits")}
                  {creditRow(t("palvelumme.advancedPool"), advancedCredits(user), POOL_MAX.strategist, "palvelumme-advanced-credits")}
                </>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">{t("palvelumme.nextReset")}</span>
                <span className="font-mono-tab text-sm text-slate-900">{unlimited ? t("palvelumme.unlimited") : fmtDate(sub.nextResetDate)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">{t("palvelumme.nextPayment")}</span>
                <span className="font-mono-tab text-sm text-slate-900">{sub.nextPaymentAt ? fmtDate(sub.nextPaymentAt) : "—"}</span>
              </div>
            </div>
          </div>
        </section>

        {/* 3. AI Team */}
        <section data-testid="palvelumme-ai-team">
          <h2 className="font-display text-2xl font-semibold text-foreground mb-1 px-1">{t("palvelumme.aiTeamTitle")}</h2>
          <p className="text-sm text-slate-500 px-1 mb-5">{t("palvelumme.aiTeamSubtitle")}</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {AI_TEAM.map(({ key, icon: Icon }) => {
              const av = roleAvailability(key);
              const poolLabel = (MODE_LEVEL[key] || "assistant") === "assistant" ? t("palvelumme.pool.standard") : t("palvelumme.pool.advanced");
              return (
                <div key={key} className="surface-card p-4 flex flex-col" data-testid={`palvelumme-role-${key}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="h-8 w-8 rounded-xl bg-accent flex items-center justify-center"><Icon className="h-4 w-4 text-slate-700" /></span>
                    <span className="font-display text-base font-semibold text-slate-900">{t(`assistant.modes.${key}.label`)}</span>
                  </div>
                  <p className="text-[13px] text-slate-600 leading-relaxed flex-1">{t(`palvelumme.roles.${key}`)}</p>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-[0.14em] text-slate-400">{poolLabel}</span>
                    <span className={[
                      "inline-flex items-center gap-1 rounded-full text-[11px] font-medium px-2 py-0.5",
                      av.tone === "ok" ? "bg-emerald-100 text-emerald-700" : av.tone === "warn" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500",
                    ].join(" ")} data-testid={`palvelumme-avail-${key}`}>
                      {av.tone === "ok" ? <CircleDot className="h-3 w-3" /> : <CircleSlash className="h-3 w-3" />}{av.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-slate-500 mt-4 px-1 italic" data-testid="palvelumme-pool-note">{t("palvelumme.poolNote")}</p>
        </section>

        {/* 4. Pricing cards */}
        <section data-testid="palvelumme-plans">
          <h2 className="font-display text-2xl font-semibold text-foreground mb-5 px-1">{t("palvelumme.pricing.title")}</h2>
          <div className="grid gap-4 md:grid-cols-3">
            {["service", "assistant", "strategist"].map((id) => {
              const P = PRICING[id];
              const Icon = P.icon;
              const current = plan === id;
              return (
                <div key={id} data-testid={`palvelumme-price-${id}`} className={["surface-card p-6 flex flex-col", current ? "ring-2 ring-primary" : ""].join(" ")}>
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5">
                      <Icon className="h-4 w-4 text-slate-700" />
                      <span className="font-display text-lg font-semibold text-slate-900">{t(`settings.plans.${id}`)}</span>
                    </span>
                    {current ? <span className="text-[9px] uppercase tracking-[0.14em] rounded-full bg-primary text-primary-foreground px-2 py-0.5">{t("assistant.currentBadge")}</span> : null}
                  </div>

                  <div className="mt-4 min-h-[68px]">
                    {id === "service" ? (
                      <p className="font-display text-3xl font-bold text-slate-900">{t("palvelumme.pricing.free")}</p>
                    ) : (
                      <>
                        <p className="font-display text-3xl font-bold text-slate-900">
                          {P.month}<span className="text-sm font-normal text-slate-500 ml-1">{t("palvelumme.pricing.perMonth")}</span>
                        </p>
                        <p className="text-sm text-slate-500 mt-0.5">{t("palvelumme.pricing.or")} {P.year} {t("palvelumme.pricing.perYear")}</p>
                      </>
                    )}
                  </div>

                  {id === "strategist" ? (
                    <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-accent border border-border text-[11px] font-medium text-slate-700 px-2.5 py-1 mb-3" data-testid="palvelumme-trial-chip">
                      <Gift className="h-3 w-3" />{t("palvelumme.pricing.trialChip")}
                    </span>
                  ) : null}

                  <ul className="mt-1 space-y-1.5 flex-1">
                    {(t(`assistant.tiers.${id}.features`) || []).map((f) => (
                      <li key={f} className="flex items-start gap-2 text-[13px] text-slate-700">
                        <Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" /><span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-5 space-y-2">
                    {id === "service" ? (
                      <Button variant="outline" size="lg" className="rounded-[20px] h-11 w-full border-border bg-card text-slate-500" disabled data-testid={`palvelumme-cta-${id}`}>
                        {current ? t("palvelumme.pricing.currentPlan") : t("palvelumme.pricing.continueFree")}
                      </Button>
                    ) : (
                      (PLAN_BUTTONS[id] || []).map((b) => (
                        <Button
                          key={b.id}
                          size="lg"
                          className="rounded-[20px] h-11 w-full justify-between px-4"
                          disabled={!stripeReady || busyPkg === b.id}
                          onClick={() => handleCheckout(b.id)}
                          data-testid={`palvelumme-checkout-${b.id}`}
                        >
                          <span className="inline-flex items-center gap-1.5">
                            {busyPkg === b.id ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                            {t(`palvelumme.pricing.${b.labelKey}`)}
                          </span>
                          <span className="font-mono-tab text-sm opacity-80">{b.price}</span>
                        </Button>
                      ))
                    )}
                    {id !== "service" && !stripeReady ? (
                      <p className="text-[11px] text-amber-600 text-center" data-testid={`palvelumme-stripe-missing-${id}`}>{t("palvelumme.pricing.stripeMissing")}</p>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 max-w-sm mx-auto" data-testid="palvelumme-discount-box">
            <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium">{t("palvelumme.pricing.discountLabel")}</label>
            <input
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
              placeholder={t("palvelumme.pricing.discountPlaceholder")}
              className="mt-1 w-full h-11 rounded-xl border border-input bg-background px-3 text-sm font-mono-tab uppercase"
              data-testid="palvelumme-discount-input"
            />
            <p className="text-[11px] text-slate-400 mt-1">{t("palvelumme.pricing.discountHint")}</p>
          </div>
          <p className="text-xs text-slate-400 mt-3 px-1 text-center" data-testid="palvelumme-price-note">{t("palvelumme.pricing.informational")}</p>
        </section>

        {/* Feature comparison matrix */}
        <section data-testid="palvelumme-compare">
          <h2 className="font-display text-2xl font-semibold text-foreground mb-5 px-1">{t("palvelumme.compare.title")}</h2>
          <div className="surface-card overflow-x-auto">
            <table className="w-full text-sm min-w-[520px]">
              <thead>
                <tr className="border-b border-border text-[11px] uppercase tracking-[0.12em] text-slate-500">
                  <th className="text-left font-medium px-4 py-3">{t("palvelumme.compare.feature")}</th>
                  <th className="font-medium px-4 py-3">{t("settings.plans.service")}</th>
                  <th className="font-medium px-4 py-3">{t("settings.plans.assistant")}</th>
                  <th className="font-medium px-4 py-3">{t("settings.plans.strategist")}</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE.map((row) => (
                  <tr key={row.k} className="border-b border-border/60 last:border-0" data-testid={`palvelumme-compare-${row.k}`}>
                    <td className="px-4 py-3 text-slate-700">{t(`palvelumme.compare.${row.k}`)}</td>
                    {row.v.map((v, i) => (<td key={`${row.k}-col${i}`} className="px-4 py-3 text-center">{cell(v)}</td>))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* 5. Trial explanation */}
        <section className="surface-card p-6" data-testid="palvelumme-trial">
          <div className="flex items-center gap-2 mb-3">
            <Gift className="h-4 w-4 text-slate-700" />
            <h2 className="font-display text-lg font-semibold text-foreground">{t("palvelumme.trialDetail.title")}</h2>
          </div>
          <ul className="space-y-2">
            {["l1", "l2", "l3"].map((k) => (
              <li key={k} className="flex items-start gap-2 text-sm text-slate-600">
                <Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" /><span>{t(`palvelumme.trialDetail.${k}`)}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* 6. Additional Information (Founder-managed; above the FAQ) */}
        {infoItems.length ? (
          <section data-testid="palvelumme-info">
            <div className="flex items-center gap-2 mb-5 px-1">
              <Info className="h-5 w-5 text-slate-700" />
              <h2 className="font-display text-2xl font-semibold text-foreground">{t("palvelumme.infoTitle")}</h2>
            </div>
            <div className="space-y-3">
              {infoItems.map((item, i) => (
                <div key={i} className="surface-card p-5" data-testid={`palvelumme-info-${i}`}>
                  {item.title ? <h3 className="font-display text-base font-semibold text-slate-900 mb-1.5">{item.title}</h3> : null}
                  {item.body ? <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{item.body}</p> : null}
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {/* 7. FAQ */}
        <section data-testid="palvelumme-faq">
          <div className="flex items-center gap-2 mb-5 px-1">
            <HelpCircle className="h-5 w-5 text-slate-700" />
            <h2 className="font-display text-2xl font-semibold text-foreground">{t("palvelumme.faq.title")}</h2>
          </div>
          <div className="space-y-2.5">
            {faqItems.map((item, i) => (
              <div key={item.q || i} className="surface-card overflow-hidden" data-testid={`palvelumme-faq-${i}`}>
                <button
                  className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left"
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  data-testid={`palvelumme-faq-toggle-${i}`}
                >
                  <span className="text-sm font-medium text-slate-900">{item.q}</span>
                  <span className={["text-slate-400 transition-transform", openFaq === i ? "rotate-45" : ""].join(" ")}>+</span>
                </button>
                {openFaq === i ? <p className="px-5 pb-4 text-sm text-slate-600 leading-relaxed">{item.a}</p> : null}
              </div>
            ))}
          </div>
        </section>

        {/* 8. Final CTA */}
        <section className="surface-card p-8 text-center" data-testid="palvelumme-cta">
          <h2 className="font-display text-2xl font-semibold text-foreground">{t("palvelumme.ctaTitle")}</h2>
          <p className="mt-2 text-sm text-slate-600 max-w-xl mx-auto">{t("palvelumme.ctaBody")}</p>
          <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button size="lg" className="btn-lift rounded-[20px] h-12 px-6" onClick={() => navigate("/settings")} data-testid="palvelumme-redeem-code">
              <Ticket className="h-4 w-4 mr-2" />{t("palvelumme.redeemCode")}
            </Button>
            <span className="text-xs text-slate-400 uppercase tracking-[0.18em]" data-testid="palvelumme-coming-soon">{t("palvelumme.comingSoon")}</span>
          </div>
        </section>

        {/* Company info (single source of truth: Company Settings) */}
        {company ? (
          <section className="surface-card p-6" data-testid="palvelumme-company">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="h-4 w-4 text-slate-700" />
              <h2 className="font-display text-lg font-semibold text-foreground">{t("palvelumme.companyTitle")}</h2>
            </div>
            <p className="font-display text-base font-semibold text-slate-900">{company.companyName || "Chronicles"}</p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2 text-sm text-slate-600">
              {company.website ? <InfoLine icon={Globe} value={company.website} href={company.website} testid="palvelumme-company-website" /> : null}
              {company.supportEmail ? <InfoLine icon={Mail} value={company.supportEmail} href={`mailto:${company.supportEmail}`} testid="palvelumme-company-support-email" /> : null}
              {company.email && !company.supportEmail ? <InfoLine icon={Mail} value={company.email} href={`mailto:${company.email}`} testid="palvelumme-company-email" /> : null}
              {company.phone ? <InfoLine icon={Phone} value={company.phone} testid="palvelumme-company-phone" /> : null}
              {company.supportHours ? <InfoLine icon={Clock} value={company.supportHours} testid="palvelumme-company-hours" /> : null}
              {company.termsUrl ? <InfoLine icon={ShieldCheck} value={t("palvelumme.terms")} href={company.termsUrl} testid="palvelumme-company-terms" /> : null}
              {company.privacyUrl ? <InfoLine icon={ShieldCheck} value={t("palvelumme.privacy")} href={company.privacyUrl} testid="palvelumme-company-privacy" /> : null}
            </div>
            {company.faq ? <p className="mt-4 text-xs text-slate-500 whitespace-pre-line">{company.faq}</p> : null}
          </section>
        ) : null}
      </main>
    </div>
  );
}

function InfoLine({ icon: Icon, value, href, testid }) {
  const content = (
    <span className="inline-flex items-center gap-2">
      <Icon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
      <span className="truncate">{value}</span>
    </span>
  );
  return href ? (
    <a href={href} target="_blank" rel="noreferrer" className="hover:text-slate-900 transition" data-testid={testid}>{content}</a>
  ) : (
    <span data-testid={testid}>{content}</span>
  );
}
